import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';

/**
 * Los links de conexión para el vendedor (contrato §10 de la tanda «Mercado Ads como canal»).
 *
 * Dos superficies con reglas distintas:
 *  - **La agencia** crea, lista y anula links con el `APIClient` de siempre (sesión, `X-Dashbo-Client`).
 *  - **Lo público** (la página `/conectar/:token` y la vuelta de Mercado Libre) lo abre un vendedor
 *    que nunca vio Dashbo, desde WhatsApp. No usa el `APIClient` a propósito: ese cliente manda el
 *    `Authorization` de la sesión que haya en el navegador, y lo público no tiene que llevar nunca la
 *    identidad de nadie (si fede lo prueba con su sesión abierta, la página tiene que comportarse
 *    igual que en el celular del vendedor). Tampoco manda `Content-Type` ni `X-Dashbo-Client` cuando
 *    no hay cuerpo: así el GET y el POST sin cuerpo son pedidos «simples» de CORS y se ahorran el
 *    preflight, que en una conexión lenta es un viaje entero de ida y vuelta.
 *
 * Las respuestas públicas tienen la MISMA forma que las del `APIClient` (`status` con el código
 * tipado, sin `data` si no es OK, `NETWORK_ERROR` si el fetch no llegó), así se guardan con `isOk()`.
 */

const AGENCY_LINKS_URL = API_URL + 'agency/connection-links/';
const PUBLIC_LINKS_URL = API_URL + 'public/connection-links/';
const PUBLIC_CALLBACK_URL = API_URL + 'public/mercado_ads/link/callback/';

export const LINK_NOT_AVAILABLE = 'LINK_NOT_AVAILABLE';
export const NETWORK_ERROR = 'NETWORK_ERROR';

/** Los clientes que el usuario puede gestionar (un no admin ve sólo los suyos: lo resuelve el backend). */
export function listAgencyClients() {
    return new APIClient(API_URL + 'agency/clients/').call('GET');
}

export function createConnectionLink(channel, agencyClientId) {
    return new APIClient(AGENCY_LINKS_URL).call('POST', {channel, agency_client_id: agencyClientId});
}

export function listConnectionLinks(agencyClientId, channel) {
    const query = new URLSearchParams({agency_client_id: agencyClientId, channel});
    return new APIClient(`${AGENCY_LINKS_URL}?${query.toString()}`).call('GET');
}

export function revokeConnectionLink(linkId) {
    return new APIClient(`${AGENCY_LINKS_URL}${encodeURIComponent(linkId)}/revoke/`).call('POST');
}

function publicCall(url, method, body) {
    const headers = {Accept: 'application/json'};
    // Sin `Referer`: la página desde la que se pide lleva el token en la URL.
    const conf = {method, headers, credentials: 'omit', referrerPolicy: 'no-referrer'};
    if (body) {
        headers['Content-Type'] = 'application/json';
        conf.body = JSON.stringify(body);
    }
    return fetch(url, conf)
        .then((response) => response.json()
            .catch(() => ({}))
            .then((payload) => {
                if (response.ok) return payload;
                // Lo que el backend tipó en `status` (LINK_NOT_AVAILABLE, MERCADO_ADS_*…), o `ERROR`.
                return {status: (payload && payload.status) || 'ERROR', http_status: response.status};
            }))
        .catch(() => ({status: NETWORK_ERROR, error_code: NETWORK_ERROR}));
}

function publicLinkUrl(token, suffix = '') {
    return `${PUBLIC_LINKS_URL}${encodeURIComponent(token)}/${suffix}`;
}

/** `{status:'OK', data:{agency_name, agency_logo_url, channel}}` o `LINK_NOT_AVAILABLE`. */
export function getPublicLink(token) {
    return publicCall(publicLinkUrl(token), 'GET');
}

/** `{status:'OK', data:{url}}`: la URL de autorización de Mercado Libre, con el `state` `lk.`. */
export function getPublicLinkAuthUrl(token) {
    return publicCall(publicLinkUrl(token, 'auth-url/'), 'POST');
}

/** La vuelta de Mercado Libre en el flujo `lk.`: `{status:'OK', data:{agency_name, nickname, advertisers_count}}`. */
export function postPublicLinkCallback(code, state) {
    return publicCall(PUBLIC_CALLBACK_URL, 'POST', {code, state});
}

// El token del link viaja de la página pública a la vuelta de Mercado Libre por `sessionStorage`
// (nunca en el `state`, nunca en `localStorage`): vive lo que vive la pestaña. Todo acceso va con
// try/catch porque hay navegadores dentro de apps (WhatsApp, Instagram) que lo bloquean, y ahí la
// conexión tiene que seguir andando: sólo se pierde el «Volver a intentar» del resultado.
const LINK_TOKEN_KEY = 'dashbo.connection_link.token';

export function rememberLinkToken(token) {
    try {
        window.sessionStorage.setItem(LINK_TOKEN_KEY, token);
    } catch (e) {
        // Sin almacenamiento: el resultado se muestra sin «Volver a intentar».
    }
}

export function readLinkToken() {
    try {
        return window.sessionStorage.getItem(LINK_TOKEN_KEY) || null;
    } catch (e) {
        return null;
    }
}

export function forgetLinkToken() {
    try {
        window.sessionStorage.removeItem(LINK_TOKEN_KEY);
    } catch (e) {
        // Nada que borrar.
    }
}

export function publicLinkPath(token) {
    return `/conectar/${encodeURIComponent(token)}`;
}
